/**
 * The title card (VTK-3398): a short text, e.g. "Dorothy And The Wizard Of Oz S2 E1", burnt into the
 * bottom-left corner of the first 5 seconds of an encode.
 *
 * The UI writes it as a one-cue WebVTT next to the title's sources (copy_file with a data: URI) and
 * requests it as the item's burnt-in subtitle. The file name carries the private-use language code,
 * which is how the repository's track detection (refresh_tracks) labels it -- a code, not a language,
 * so it is never confused with a real subtitle and is matched exactly by the delivery check.
 */

// At most 8 characters after "x-": VTK's caption renderer parses the code with langcodes, which
// rejects longer subtags (so not "zxx-x-titlecard").
export const TITLECARD_LANG = 'zxx-x-titlcrd';
export const TITLECARD_FILENAME = `titlecard.${TITLECARD_LANG}.vtt`;
export const TITLECARD_MAX_LENGTH = 120;
// Fixed by the user (VTK-3398): 0-5 s, bottom-left. Percentages, because VTK's burn-in renderer
// (castlabs-pycaption, burntin_subtitle_position "source") reads only "line:NN%" -- a "line:-1" is
// ignored and the card ends up centred -- and fails on "position:0%" (the text outline runs off screen).
export const TITLECARD_CUE_SETTINGS = 'line:88% position:2% align:start';
export const TITLECARD_BURNIN_POSITION = 'source';

/**
 * A starting text from the source folder name: "DorothyAndTheWizardOfOz_S2E1_TH" becomes
 * "Dorothy And The Wizard Of Oz S2 E1". The operator edits it; nothing else is inferred.
 */
export function titlecardPrefill(inputBrefix: string): string {
  const folder = (inputBrefix || '').replace(/\/+$/, '').split('/').pop() || '';
  const parts = folder.split(/[_\s]+/).filter(p => p !== '');
  // A trailing short all-caps tag such as "TH" or "TR" marks the delivery, not the title.
  if (parts.length > 1 && /^[A-Z]{1,3}$/.test(parts[parts.length - 1])) {
    parts.pop();
  }
  return parts
    .map(p => {
      const se = /^S(\d+)E(\d+)$/i.exec(p);
      if (se) {
        return `S${Number(se[1])} E${Number(se[2])}`;
      }
      return p.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');
    })
    .join(' ')
    .trim();
}

/**
 * The card's text as safe WebVTT cue payload: one line, no cue timing arrow, markup characters escaped.
 * A blank line would end the cue, and "-->" would be read as a timing line.
 */
export function titlecardCueText(text: string): string {
  return (text || '')
    .replace(/\s+/g, ' ')
    .replace(/-->/g, '→')
    .trim()
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function titlecardVtt(text: string): string {
  const cue = titlecardCueText(text);
  if (!cue) {
    throw new Error('The title card text is empty.');
  }
  return `WEBVTT\n\n00:00:00.000 --> 00:00:05.000 ${TITLECARD_CUE_SETTINGS}\n${cue}\n`;
}

export function titlecardDataUri(text: string): string {
  const bytes = new TextEncoder().encode(titlecardVtt(text));
  let binary = '';
  bytes.forEach(b => binary += String.fromCharCode(b));
  return `data:text/vtt;charset=utf-8;base64,${btoa(binary)}`;
}

/** Where the card goes: next to the title's sources, under a fixed name. */
export function titlecardDestination(inputBrefix: string): string {
  return `s3://${(inputBrefix || '').replace(/^s3:\/\//, '').replace(/\/+$/, '')}/${TITLECARD_FILENAME}`;
}

/**
 * Languages to preselect for a new PO item, without the title card: a card left next to the sources by an
 * earlier submit is a subtitle track of its own (zxx-x-titlcrd), and must not be delivered as a soft subtitle.
 */
export function withoutTitlecard(languages: string[]): string[] {
  return (languages || []).filter(l => l !== TITLECARD_LANG);
}
